import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/shared/ui/dialog";
import { FormControl, FormField, FormItem, FormLabel } from "@/shared/ui/form";
import { Input } from "@/shared/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/shared/ui/table";
import { useForm, useFormContext } from "react-hook-form";
import { getOpenAdminList, type OpenAdminListItem } from "@/server-action/admin/get-open-admin-list";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import { useState } from "react";
import { SignupFormValues } from "@/shared/lib/validation/user-schemas";
import { Ico18DoubleArrowRight } from "@/shared/assets/icons/ico_18_double_arrow_right";
import { cn } from "@/shared/lib";
import { ScrollArea } from "@/shared/ui/scroll-area";
import { useTranslations } from "next-intl";

const PAGE_SIZE = 10;
const PAGE_GROUP_SIZE = 5;

export function RepresentativeSearchDialog() {
  const t = useTranslations();
  const parentForm = useFormContext<SignupFormValues>();
  const [currentPage, setCurrentPage] = useState(1);
  const [adminList, setAdminList] = useState<{ items: OpenAdminListItem[]; totalCount: number }>({
    items: [],
    totalCount: 0,
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // pagination
  const totalPages = Math.max(1, Math.ceil(adminList.totalCount / PAGE_SIZE));
  const currentGroup = Math.ceil(currentPage / PAGE_GROUP_SIZE);
  const startPage = (currentGroup - 1) * PAGE_GROUP_SIZE + 1;
  const endPage = Math.min(startPage + PAGE_GROUP_SIZE - 1, totalPages);

  const onClickSearch = async () => {
    const searchValue = form.getValues("representative");
    const result = await getOpenAdminList({
      page: 1,
      pageSize: PAGE_SIZE,
      name: searchValue,
    });
    if (!result.ok) {
      throw new Error(result.error.message);
    }
    const { items, totalCount } = result.data;
    setAdminList({ items, totalCount });
    setHasSearched(true);
    setCurrentPage(1);
  };

  const handlePageChange = async (page: number) => {
    const searchValue = form.getValues("representative");
    const result = await getOpenAdminList({
      page,
      pageSize: PAGE_SIZE,
      name: searchValue,
    });
    if (!result.ok) {
      throw new Error(result.error.message);
    }
    const { items, totalCount } = result.data;
    setAdminList({ items, totalCount });
    setCurrentPage(page);
  };

  const form = useForm({
    defaultValues: {
      representative: "",
    },
  });

  const handleDialogChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // 다이얼로그가 닫힐 때 초기 상태로 리셋
      setCurrentPage(1);
      setAdminList({ items: [], totalCount: 0 });
      setHasSearched(false);
      form.reset();
    }
  };

  const handleAdminSelect = (admin: OpenAdminListItem) => {
    // 부모 폼의 adminId에 선택된 관리자 ID 설정 (실제 저장값)
    parentForm.setValue("adminId", admin.id);
    // adminName 필드에 이름 설정 (화면 표시용)
    parentForm.setValue("adminName", admin.name);
    // 다이얼로그 닫기
    handleDialogChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleDialogChange}>
      <DialogTrigger asChild>
        <Button variant="input" size="input">
          {t("f_ei_btn_rep")}
        </Button>
      </DialogTrigger>
      <DialogContent
        variant="public"
        showCloseButton={false}
        className="pc:!max-w-[680px] tb:!max-w-[720px] !max-w-[328px]"
      >
        <DialogHeader>
          <DialogTitle variant="public" className="tb:mb-0 mb-[22px] text-center">
            {t("f_rf_title")}
          </DialogTitle>
        </DialogHeader>

        <div className="tb:gap-8 flex flex-col gap-5">
          <FormField
            control={form.control}
            name="representative"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="body-md-bold text-gray-900">{t("f_rf_lable_rep")}</FormLabel>
                <FormControl>
                  <div className="tb:gap-2 flex w-full items-center gap-[6px]">
                    <Input variant="public" placeholder={t("f_rf_placeholder_rep")} {...field} />

                    <Button variant="input" size="input" onClick={onClickSearch}>
                      {t("f_rf_btn_search")}
                    </Button>
                  </div>
                </FormControl>
              </FormItem>
            )}
          />

          <ScrollArea className={`tb:h-auto ${!hasSearched || adminList.items.length === 0 ? "h-auto" : "h-[228px]"}`}>
            <Table className="table-fixed">
              <TableHeader>
                <TableRow className="border-gray-900">
                  <TableHead className="body-md-bold tb:py-[10px] h-fit py-[6px] text-center" variant="public">
                    {t("f_rf_list_header_name")}
                  </TableHead>
                  <TableHead className="body-md-bold tb:py-[10px] h-fit py-[6px] text-center" variant="public">
                    {t("f_rf_list_header_phone")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!hasSearched ? (
                  <TableRow className="border-gray-100">
                    <TableCell
                      colSpan={2}
                      className="body-md-regular tb:py-15 px-0 py-[50px] text-center text-gray-400"
                    >
                      {t("f_rf_list_default")}
                    </TableCell>
                  </TableRow>
                ) : adminList.items.length === 0 ? (
                  <TableRow className="border-gray-100">
                    <TableCell
                      colSpan={2}
                      className="body-md-regular tb:py-15 px-0 py-[50px] text-center text-gray-400"
                    >
                      {t("f_rf_list_null")}
                    </TableCell>
                  </TableRow>
                ) : (
                  adminList.items.map((admin) => (
                    <TableRow
                      key={admin.id}
                      className="cursor-pointer border-gray-100 hover:bg-gray-50"
                      onClick={() => handleAdminSelect(admin)}
                    >
                      <TableCell className="body-md-regular tb:py-[10px] px-0 py-[6px] text-center text-gray-500">
                        {admin.name}
                      </TableCell>
                      <TableCell className="body-md-regular tb:py-[10px] px-0 py-[6px] text-center text-gray-500">
                        {admin.phone || "-"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
          {!hasSearched || adminList.items.length === 0 ? null : (
            <Pagination>
              <PaginationContent>
                {/* << 버튼 (이전 페이지 그룹) */}
                <PaginationItem>
                  <PaginationLink
                    onClick={
                      startPage > 1 && hasSearched && adminList.items.length > 0
                        ? () => handlePageChange(startPage - 1)
                        : undefined
                    }
                    className={cn(
                      "tb:mr-2 mr-1",
                      startPage <= 1 || !hasSearched || adminList.items.length === 0
                        ? "pointer-events-none text-gray-300"
                        : "text-gray-700",
                    )}
                  >
                    <Ico18DoubleArrowRight className="mr-2 rotate-180" />
                  </PaginationLink>
                </PaginationItem>

                <PaginationItem>
                  <PaginationPrevious
                    onClick={
                      currentPage > 1 && hasSearched && adminList.items.length > 0
                        ? () => handlePageChange(currentPage - 1)
                        : undefined
                    }
                    className={cn(
                      currentPage <= 1 || !hasSearched || adminList.items.length === 0
                        ? "pointer-events-none text-gray-300"
                        : "text-gray-700",
                    )}
                  />
                </PaginationItem>
                {/* 페이지 번호 버튼 */}
                <div className="tb:mx-6 tb:gap-2 mx-[10px] flex gap-1">
                  {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map((page) => (
                    <PaginationItem key={page} className="">
                      <PaginationLink isActive={page === currentPage} onClick={() => handlePageChange(page)}>
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                </div>
                <PaginationItem>
                  <PaginationNext
                    onClick={
                      currentPage < totalPages && hasSearched && adminList.items.length > 0
                        ? () => handlePageChange(currentPage + 1)
                        : undefined
                    }
                    className={cn(
                      currentPage >= totalPages || !hasSearched || adminList.items.length === 0
                        ? "pointer-events-none text-gray-300"
                        : "text-gray-700",
                    )}
                  />
                </PaginationItem>
                {/* >> 버튼 (다음 페이지 그룹) */}
                <PaginationItem>
                  <PaginationLink
                    onClick={
                      endPage < totalPages && hasSearched && adminList.items.length > 0
                        ? () => handlePageChange(endPage + 1)
                        : undefined
                    }
                    className={cn(
                      "tb:ml-2 ml-1",
                      endPage >= totalPages || !hasSearched || adminList.items.length === 0
                        ? "pointer-events-none text-gray-300"
                        : "text-gray-700",
                    )}
                  >
                    <Ico18DoubleArrowRight />
                  </PaginationLink>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>

        <DialogFooter className="!justify-center">
          <Button variant="primary" size="lg" onClick={() => handleDialogChange(false)} className="tb:mt-0 mt-6">
            {t("f_rf_btn_close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
